import type { UserAction } from '../actions/login-actions';
import type {
  Action,
  CheckUserNameIdentities,
  CheckUserNamePasswordlessType,
  CheckUserNameRedirectType,
  JWT,
  LoginFieldError,
  LoginGlobalError,
  RemoveRememberedAccountResponseTypes,
} from '../types';
import type { RememberedAccount } from './login-reducer';

export interface State {
  fieldErrors: LoginFieldError;
  error?: LoginGlobalError;
  restrictedEmail: boolean;
  recoveryToken?: JWT;
  rememberMeEnabled: boolean;
  rememberedAccounts: RememberedAccount[];
  isRememberMeChecked: boolean;
  rememberedAccountsResponseCodes: RemoveRememberedAccountResponseTypes[];
  isCheckingUsername: boolean;
  isUsernameChecked: boolean;
  userActions: UserAction[];
  selectedUserAction: UserAction | null;
  isExecutingUserAction: boolean;
  redirectUrl: string;
  redirectType: CheckUserNameRedirectType | null;
  isLoggingIn: boolean;
  nextAttempt: number;
  showResetHint: boolean;
  isCaptchaRequired: boolean;
}

export const defaultState: State = {
  fieldErrors: {},
  restrictedEmail: false,
  rememberMeEnabled: false,
  rememberedAccounts: [],
  isRememberMeChecked: false,
  rememberedAccountsResponseCodes: [],
  isCheckingUsername: false,
  isUsernameChecked: false,
  userActions: [],
  selectedUserAction: null,
  isExecutingUserAction: false,
  redirectUrl: '',
  redirectType: null,
  isLoggingIn: false,
  nextAttempt: 1,
  showResetHint: false,
  isCaptchaRequired: false,
};

const getAutoSelectedUserAction = (userActions: UserAction[]): UserAction | null => {
  if (userActions.length !== 1) {
    return null;
  }

  const [userAction] = userActions;
  return userAction.action === 'not_permitted' ? null : userAction;
};

const getFailedState = (
  state: State,
  action: Extract<Action, { type: 'LOGIN_REQUEST_CHECK_USERNAME_V2_FAILED' }>
): State => ({
  ...state,
  isCheckingUsername: false,
  isUsernameChecked: false,
  isExecutingUserAction: false,
  userActions: [],
  selectedUserAction: null,
  fieldErrors: action.fieldErrors || {},
  error: action.error,
});

const getSelectedActionState = (state: State, userAction: UserAction): State => {
  switch (userAction.action) {
    case 'redirect':
      return {
        ...state,
        selectedUserAction: userAction,
        redirectUrl: userAction.redirect_uri,
        redirectType: userAction.redirect_type,
      };
    case 'ic_redirect':
      return {
        ...state,
        selectedUserAction: userAction,
        redirectUrl: userAction.redirect_uri,
        redirectType: null,
      };
    case 'signup':
      return {
        ...state,
        selectedUserAction: userAction,
        redirectUrl: '',
        redirectType: null,
      };
    case 'not_permitted':
      return {
        ...state,
        selectedUserAction: userAction,
        error: { code: userAction.error_code },
      };
    default:
      return {
        ...state,
        selectedUserAction: userAction,
        redirectUrl: '',
        redirectType: null,
      };
  }
};

const loginV2 = (state: State = defaultState, action: Action): State => {
  switch (action.type) {
    case 'RESET_APP_STATE': {
      const loginState = action.data.login;
      return {
        ...defaultState,
        fieldErrors: loginState?.fieldErrors || defaultState.fieldErrors,
        error: loginState?.error,
        restrictedEmail: loginState?.restrictedEmail || defaultState.restrictedEmail,
        recoveryToken: loginState?.recoveryToken,
        rememberMeEnabled: loginState?.rememberMeEnabled || defaultState.rememberMeEnabled,
        rememberedAccounts: loginState?.rememberedAccounts || defaultState.rememberedAccounts,
        isRememberMeChecked: loginState?.isRememberMeChecked || defaultState.isRememberMeChecked,
        rememberedAccountsResponseCodes:
          loginState?.rememberedAccountsResponseCodes ||
          defaultState.rememberedAccountsResponseCodes,
        isLoggingIn: loginState?.isLoggingIn || defaultState.isLoggingIn,
        nextAttempt: loginState?.nextAttempt || defaultState.nextAttempt,
        showResetHint: loginState?.showResetHint || defaultState.showResetHint,
        isCaptchaRequired: loginState?.isCaptchaRequired || defaultState.isCaptchaRequired,
      };
    }
    case 'LOGIN_REQUEST_CHECK_USERNAME_V2':
      return {
        ...state,
        isCheckingUsername: true,
        isUsernameChecked: false,
        isExecutingUserAction: false,
        fieldErrors: {},
        error: undefined,
        userActions: [],
        selectedUserAction: null,
        redirectUrl: '',
        redirectType: null,
      };
    case 'LOGIN_REQUEST_CHECK_USERNAME_V2_SUCCEEDED': {
      const selectedUserAction = getAutoSelectedUserAction(action.userActions);
      const nextState = {
        ...state,
        isCheckingUsername: false,
        isUsernameChecked: true,
        fieldErrors: {},
        error:
          action.userActions.length === 0
            ? { code: 'unknown' as const }
            : action.userActions.length === 1 && action.userActions[0].action === 'not_permitted'
            ? { code: action.userActions[0].error_code }
            : undefined,
        userActions: action.userActions,
        selectedUserAction,
      };

      return selectedUserAction ? getSelectedActionState(nextState, selectedUserAction) : nextState;
    }
    case 'LOGIN_REQUEST_CHECK_USERNAME_V2_FAILED':
      return getFailedState(state, action);
    case 'LOGIN_SELECT_CHECK_USERNAME_V2_USER_ACTION':
      return {
        ...state,
        selectedUserAction: action.userAction,
      };
    case 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION':
      return {
        ...getSelectedActionState(state, action.userAction),
        isExecutingUserAction: true,
        error: undefined,
      };
    case 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION_SUCCEEDED':
      return {
        ...state,
        isExecutingUserAction: false,
        redirectUrl: action.redirectUrl || state.redirectUrl,
        redirectType: action.redirectType === undefined ? state.redirectType : action.redirectType,
      };
    case 'LOGIN_EXECUTE_CHECK_USERNAME_V2_USER_ACTION_FAILED':
      return {
        ...state,
        isExecutingUserAction: false,
        error: action.error,
      };
    case 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN':
      return {
        ...state,
        isLoggingIn: true,
        fieldErrors: {},
        error: undefined,
      };
    case 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN_SUCCESS':
      return {
        ...state,
        isLoggingIn: false,
        fieldErrors: {},
        redirectUrl: action.redirectUrl,
      };
    case 'LOGIN_REQUEST_USERNAME_PASSWORD_LOGIN_FAILED':
      return {
        ...state,
        isLoggingIn: false,
        error: action.error,
      };
    case 'LOGIN_REQUEST_PASSWORDLESS_LOGIN':
      return {
        ...state,
        isLoggingIn: true,
        fieldErrors: {},
      };
    case 'LOGIN_REQUEST_PASSWORDLESS_LOGIN_FAILED':
      return {
        ...state,
        isLoggingIn: false,
        error: action.error,
      };
    case 'LOGIN_REQUEST_PASSWORDLESS_LOGIN_SUCCESS':
      return {
        ...state,
        isLoggingIn: false,
        fieldErrors: {},
      };
    case 'LOGIN_REDIRECT_USER':
      return {
        ...state,
        isLoggingIn: false,
        fieldErrors: {},
        redirectUrl: action.redirectUrl,
      };
    case 'LOGIN_UPDATE_CAPTCHA_REQUIREMENT':
      return {
        ...state,
        isCaptchaRequired: action.isCaptchaRequired,
      };
    case 'UPDATE_LOGIN_ATTEMPT_DETAILS':
      return {
        ...state,
        nextAttempt: action.nextAttempt,
      };
    case 'LOGIN_SHOW_LOGIN_FORM_ERRORS':
      return {
        ...state,
        fieldErrors: action.errors,
      };
    case 'REMEMBER_ME_CHECKBOX_UPDATE':
      return {
        ...state,
        isRememberMeChecked: action.payload.enabled,
      };
    case 'REMEMBER_ME_CHECKBOX_TOGGLE':
      return {
        ...state,
        isRememberMeChecked: !state.isRememberMeChecked,
      };
    case 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_INITIATE':
      return {
        ...state,
        rememberedAccounts: state.rememberedAccounts.filter(
          account => !action.payload.accountIds.includes(account.accountId)
        ),
      };
    case 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_SUCCESS':
    case 'REMOVE_REMEMBERED_ACCOUNT_REQUEST_FAILED':
      return {
        ...state,
        rememberedAccountsResponseCodes: [
          ...state.rememberedAccountsResponseCodes,
          action.payload.response,
        ],
      };
    case 'RESET_REMEMBERED_ACCOUNT_RESPONSE_CODES':
      return {
        ...state,
        rememberedAccountsResponseCodes: [],
      };
    case 'RESET_REMEMBER_ME_STATE':
    case 'LOGIN_RESET_V2':
      return defaultState;
    default:
      return state;
  }
};

export const getPasswordless = (state: State): CheckUserNamePasswordlessType | undefined => {
  const selectedUserAction = state.selectedUserAction;

  if (selectedUserAction?.action === 'no_action' || selectedUserAction?.action === 'social_login') {
    return selectedUserAction.passwordless;
  }

  return undefined;
};

export const getIdentities = (state: State): CheckUserNameIdentities | undefined => {
  const selectedUserAction = state.selectedUserAction;

  if (selectedUserAction?.action === 'social_login') {
    return selectedUserAction.identities;
  }

  return undefined;
};

export default loginV2;
